// ── One-off charge on the card on file ───────────────────────
//
// MOVED OUT OF app.js UNCHANGED (standing rule ③: extract what you touch, and
// MOVE and CHANGE are separate commits). Everything below the deps block is
// the app.js text as it stood — same element ids, same wording, same key
// hygiene, same window.* names, which is what the modal's inline onclick=
// handlers in index.html still call.
//
// The one new line is how it reaches the roster: app.js keeps its own
// `clients` array (the VISIBLE set — not the same array as state.js's), so the
// lookup is injected rather than silently re-pointed at a different list.

import { callFunction } from './api.js';
import * as _state from './state.js';
import * as _stripe from './stripe.js';
import { escHtml, showToast, formatCurrency } from './utils.js';
import {
  resetChargeModes, loadBalanceForModal, setChargeMode, chargeMode,
  refreshChargeUi, owedRows, centsTyped, chargeModeTimeZone,
} from './amount-owed/balance-mode.js';
import { balanceCharged, balanceChargeFailed } from './amount-owed/outcomes.js';
import { balanceHint } from './amount-owed/money.js';
import { refreshAmountOwedBlock } from './amount-owed/owed-block.js';

/** @type {{ getClients: () => any[] }} */
let _deps = { getClients: () => [] };

/** @param {{ getClients?: () => any[] }} d */
export function setChargeOneoffDeps(d) { _deps = { ..._deps, ...d }; }

/** app.js's own roster array — see the header. */
const clientsList = () => _deps.getClients() || [];

// The manual twin (openRecordPaymentModal) LOGS a payment that already
// happened; this one MAKES the payment — client-charge-oneoff.js invoices the
// saved card. History, totals and the receipt email arrive via the
// stripe-webhook pipeline, so nothing is booked optimistically here: the
// webhook is the ledger.
//
// New purchase, part 3b (spec §9): `{ balanceOnly: true }` is "Pay with another
// card" from the Amount owed box: the balance side on its own, titled "Pay
// what Sam owes", with no switch. Nothing else about the window changes.
/** Each opening of the window, so a balance lookup that lands after the window was opened again is ignored. */
let _opens = 0;
window.openChargeOneoffModal = function(clientId, opts = {}) {
  const c = clientsList().find(x => x.id === clientId);
  if (!c) return;
  const balanceOnly = !!(opts && opts.balanceOnly);
  const title = document.getElementById('co-title');
  const first = c.firstName || String(c.name || '').trim().split(' ')[0] || 'this client';
  if (title) title.textContent = balanceOnly ? `Pay what ${first} owes` : `Charge ${c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'client'}`;
  document.querySelector('#charge-oneoff-overlay .session-fmt-toggle')?.classList.toggle('hidden', balanceOnly);
  // The mockup's Pay what Sam owes has no "What is this for?": the balance side writes that line itself
  document.getElementById('co-description')?.closest('.form-group')?.classList.toggle('hidden', balanceOnly);
  document.getElementById('co-amount').value = '';
  document.getElementById('co-description').value = '';
  document.getElementById('co-client-id').value = clientId;
  // Back to New payment with the balance side dead, then ask what they owe.
  // The lookup never blocks the modal: a coach opening this to take a new
  // payment must not wait on it.
  resetChargeModes({ balanceOnly });
  const opening = ++_opens;
  loadBalanceForModal(clientId).then(() => { if (balanceOnly && opening === _opens) setChargeMode('balance'); });
  window.oneoffAmountChanged();
  _loadOneoffCards(clientId);
  // Minted per modal-open and sent as the Stripe idempotency key, so a
  // double-click or a timed-out retry can never charge twice.
  document.getElementById('co-charge-key').value =
    (crypto.randomUUID ? crypto.randomUUID() : `ck-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const errEl = document.getElementById('co-error');
  if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
  const btn = document.getElementById('co-submit-btn');
  // Pay with another card: off until the balance arrives and its side is on (balance-mode.js refreshChargeUi)
  if (btn) { btn.disabled = balanceOnly; delete btn.dataset.busy; }
  document.getElementById('charge-oneoff-overlay').classList.remove('hidden');
};

// The primary button reads the money: "Charge $250". The last thing a coach
// reads before committing a charge is the amount itself — Stripe's own
// pattern, and the cheapest possible guard against a mistyped amount. Owner,
// 2026-09-18: on TWO lines now, with the card underneath, and whole dollars
// without cents. balance-mode.js owns the label, the live line and whether
// the amount may be sent at all.
window.oneoffAmountChanged = function() {
  refreshChargeUi();
};

/** The switch at the top of the modal. @param {'new'|'balance'} mode */
window.chargeModeChanged = function(mode) {
  setChargeMode(mode);
};

window.closeChargeOneoffModal = function() {
  document.getElementById('charge-oneoff-overlay').classList.add('hidden');
};

// Saved cards → radio rows + a "New card" row that reveals a Stripe card
// field. The preselected radio is the same card the server would pick on its
// own (default, else newest), so doing nothing changes nothing.
async function _loadOneoffCards(clientId) {
  const list = document.getElementById('co-card-list');
  const newWrap = document.getElementById('co-new-card');
  if (newWrap) newWrap.classList.add('hidden');
  if (!list) return;
  list.innerHTML = '<div class="co-card-loading">Loading cards on file…</div>';
  try {
    const res = await callFunction('client-payment-methods', { clientId });
    const cards = res.cards || [];
    const oneOff = (res.usage || []).find(u => u.label === 'One-off charges');
    const rows = cards.map(card => `
      <label class="co-card-row">
        <input type="radio" name="co-card" value="${escHtml(card.id)}" ${card.id === oneOff?.pmId ? 'checked' : ''} onchange="window.oneoffCardPicked(false)" />
        <span class="co-card-brand">${escHtml(card.brand)}</span>
        <span class="co-card-num">•••• ${escHtml(card.last4)}</span>
        ${card.isDefault ? '<span class="co-card-default">Default</span>' : ''}
        <span class="co-card-exp">${card.expMonth ? `exp ${card.expMonth}/${String(card.expYear).slice(-2)}` : ''}</span>
      </label>`).join('');
    list.innerHTML = rows + `
      <label class="co-card-row">
        <input type="radio" name="co-card" value="__new__" ${cards.length === 0 ? 'checked' : ''} onchange="window.oneoffCardPicked(true)" />
        <span class="co-card-label">New card</span>
        <span class="co-card-exp">enter details below</span>
      </label>`;
    if (cards.length === 0) window.oneoffCardPicked(true);
  } catch (e) {
    // The picker failing must not block a charge — the server falls back to
    // the default card when no pick is sent.
    list.innerHTML = '<div class="co-card-loading">Couldn\'t load the card list — the default card on file will be used.</div>';
  }
}

window.oneoffCardPicked = function(isNew) {
  const newWrap = document.getElementById('co-new-card');
  if (!newWrap) return;
  newWrap.classList.toggle('hidden', !isNew);
  if (isNew) _stripe.mountOneoffCardElement();
  // The button's second line names the card that was just picked.
  refreshChargeUi();
};

window.handleChargeOneoff = async function() {
  const clientId    = document.getElementById('co-client-id').value;
  const amount      = parseFloat(document.getElementById('co-amount').value);
  const description = document.getElementById('co-description').value.trim();
  const chargeKey   = document.getElementById('co-charge-key').value;

  const errEl = document.getElementById('co-error');
  const btn   = document.getElementById('co-submit-btn');
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = ''; } };

  const balance = chargeMode() === 'balance';

  if (!amount || amount <= 0) return showErr('Enter the amount to charge.');
  // ⚠️ THE BALANCE SIDE HAS ITS OWN CEILING AND ITS OWN RECEIPT LINE. The
  // server refuses an overpay with a 400; refusing it here means the card is
  // never touched for one. The description is the server's on this side, so
  // the coach is never asked for it.
  if (balance) {
    const hint = balanceHint(owedRows(), centsTyped(), { timeZone: chargeModeTimeZone() });
    if (!hint.ok) return showErr(hint.sentence || 'Enter the amount to charge.');
  } else if (!description) {
    return showErr("Say what this is for — it appears on the client's receipt.");
  }

  if (btn) { btn.disabled = true; btn.dataset.busy = '1'; btn.textContent = 'Charging…'; }
  try {
    // Which card: a saved pick rides along as its pm id; "New card" creates a
    // PaymentMethod from the card field first (never touches our servers as
    // raw digits), and the server attaches it — which is what saves it to the
    // client's file. No radio rendered (list failed) → server picks default.
    const picked = document.querySelector('input[name="co-card"]:checked')?.value || '';
    let paymentMethodId;
    if (picked === '__new__') {
      const el = _state.oneoffCardElement;
      if (!el) throw new Error('Card field not ready — pick a saved card or reopen the modal.');
      const { paymentMethod, error: pmErr } = await _state.stripe.createPaymentMethod({ type: 'card', card: el });
      if (pmErr) throw new Error(pmErr.message);
      paymentMethodId = paymentMethod.id;
    } else if (picked) {
      paymentMethodId = picked;
    }
    // ⚠️ `purpose` IS OMITTED ENTIRELY ON THE NEW-PAYMENT SIDE, and so is the
    // description on the balance side. The server defaults purpose to 'new'
    // and writes its own receipt line for a balance.
    const body = balance
      ? { clientId, amount, purpose: 'balance', chargeKey, paymentMethodId }
      : { clientId, amount, description, chargeKey, paymentMethodId };
    const res = await callFunction('client-charge-oneoff', body);
    if (btn) delete btn.dataset.busy;
    window.closeChargeOneoffModal();
    if (balance) {
      const out = balanceCharged(res, { owed: owedRows(), timeZone: chargeModeTimeZone() });
      showToast(out.lines.join(' '), 'success');
      // The profile's Amount owed block is looking at a balance that just
      // changed — re-read it rather than leave a stale number on screen.
      refreshAmountOwedBlock(clientId);
    } else {
      showToast(`Charged ${formatCurrency(res.amount)}${res.last4 ? ` to card ending ${res.last4}` : ''}. Receipt is on its way.`, 'success');
    }
    // The history row + totals land via the webhook (usually a second or
    // two); a refresh here would show the pre-charge numbers and read like
    // the charge didn't take. renderCurrentView on the next natural refresh
    // picks it up — the toast is the confirmation.
  } catch (err) {
    // Key hygiene, the S63-doubling lesson applied to money:
    // - TRANSPORT failure (fetch died, outcome unknown) → KEEP the key. If the
    //   charge actually landed, the retry replays it instead of charging twice.
    // - SERVER-spoken error (decline, validation) → the outcome is known and
    //   the invoice was voided, so a retry needs a FRESH key; replaying the old
    //   one would resurrect a voided invoice and error out.
    // Mis-classification fails safe: worst case is a visible error on retry,
    // never a second charge.
    //
    // ⚠️ AND ONE SERVER-SPOKEN ERROR KEEPS THE KEY TOO. The balance 503 that
    // says "Charged $200.00 … Press the same button again to finish applying
    // it" means the money is ALREADY COLLECTED and only the step that lowers
    // the invoice failed. Pressing again must replay the SAME key; a fresh one
    // takes a second payment. balanceChargeFailed is what recognises it.
    //
    // ⚠️ AND AN ANSWER THAT COULD NOT BE READ IS AN UNKNOWN OUTCOME (2026-10-06).
    // callFunction throws with `payload: null` when the body is not JSON:
    // Cloudflare swaps an origin 502 for its own page, and a gateway timeout can
    // land after Stripe took the money. The charge may have landed, so keep the
    // key, the same as a transport failure.
    const transport = /failed to fetch|networkerror|network request|load failed|timed? ?out/i.test(String(err?.message || ''));
    const unread = !!err && err.payload === null;
    const out = balance ? balanceChargeFailed(err) : null;
    if (!transport && !unread && !out?.reuseChargeKey) {
      document.getElementById('co-charge-key').value =
        (crypto.randomUUID ? crypto.randomUUID() : `ck-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }
    showErr(out ? out.lines.join(' ') : (err.message || 'Charge failed. The card was not charged.'));
    if (btn) { btn.disabled = false; delete btn.dataset.busy; window.oneoffAmountChanged(); }
  }
};
